import React from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { VseHtmlField } from '../VseHtml/VseHtmlField';
import { VseTinymceEditorDialog, VseTinymceEditorDialogProps } from '../VseHtml/VseTinymceEditorDialog';
import { VseCourseEditTemplate } from '../types/VseCourseEditTemplate';
import {
  CourseStatusEntity,
  LanguageEntity,
} from '../types';
import { VseCourseEditMode } from './types';
import { VseTextField } from '../utils/VseTextField';
import { VseNumberField } from '../utils/VseNumberField';
import { VseSelectField } from '../utils/VseSelectField';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
  },
}));

export type CourseErrorKeyResolver = (key: keyof VseCourseEditTemplate) => string;

export interface VseCourseEditGridProps {
  data: VseCourseEditTemplate,
  mode?: VseCourseEditMode,
  readOnly?: boolean,
  decodeSeason: (id: number) => string | undefined,
  decodeUniversity: (id: number) => string | undefined,
  decodeUniversityTerm: (seasonID: number, universityID: number, termCode: string) => string | undefined,
  courseStatusOptions: CourseStatusEntity[],
  languageOptions: LanguageEntity[],
  onChange: (changes: Partial<VseCourseEditTemplate>) => void,
  resolveCourseErrorKey: CourseErrorKeyResolver,
  valProblemKeys?: Array<string>,
}

export function VseCourseEditGrid(props: VseCourseEditGridProps) {
  const theme = useTheme();
  const classes = useStyles(theme);
  const firstDayOfClass = props.data.firstDayOfClass ? props.data.firstDayOfClass.slice(0, 10) : undefined;
  const lastDayOfClass = props.data.lastDayOfClass ? props.data.lastDayOfClass.slice(0, 10) : undefined;
  const [editorProps, setEditorProps] = React.useState<VseTinymceEditorDialogProps>({
    open: false,
  });

  function hasError(key: keyof VseCourseEditTemplate): boolean {
    if (props.valProblemKeys) {
      const searchElement = props.resolveCourseErrorKey(key);
      return props.valProblemKeys.includes(searchElement);
    }
    return false;
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid container>
          <Grid item xs={12} md={3}>
            <VseTextField
              label='VSE Season'
              required
              readOnly={true}
              value={props.decodeSeason(props.data.seasonID)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <VseTextField
              label='Course Offering University'
              required
              readOnly={true}
              value={props.decodeUniversity(props.data.univID)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <VseTextField
              label='Course Offering Term / Semester'
              required
              readOnly={true}
              value={props.decodeUniversityTerm(props.data.seasonID, props.data.univID, props.data.termCode)}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={3}>
            <VseTextField
              label='Course Code'
              required
              readOnly={props.readOnly}
              value={props.data.courseCode}
              onChange={(e) => props.onChange({ courseCode: e.target.value })}
              error={hasError('courseCode')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <VseTextField
              label='Course Title'
              required
              readOnly={props.readOnly}
              value={props.data.courseTitle}
              onChange={(e) => props.onChange({ courseTitle: e.target.value })}
              error={hasError('courseTitle')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <VseSelectField
              label='Status'
              required
              readOnly={props.readOnly || props.mode!=='centralOffice'}
              value={props.data.statusCode}
              options={props.courseStatusOptions}
              onChange={(e) => props.onChange({ statusCode: e.target.value })}
              error={hasError('statusCode')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={6}>
            <VseTextField
              label='Course Offering Faculty/School/Department'
              required
              readOnly={props.readOnly}
              value={props.data.offerUnit}
              onChange={(e) => props.onChange({ offerUnit: e.target.value })}
              error={hasError('offerUnit')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <VseTextField
              label='Name of Course Teacher'
              required
              readOnly={props.readOnly}
              value={props.data.courseTeacher}
              onChange={(e) => props.onChange({ courseTeacher: e.target.value })}
              error={hasError('courseTeacher')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="link"
              label='Link to Teacher Profile'
              readOnly={props.readOnly}
              value={props.data.teacherProfile}
              onUpdated={(content) => {
                props.onChange({ teacherProfile: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('teacherProfile')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={2}>
            <VseNumberField
              label='No. of Credits / Units'
              required
              readOnly={props.readOnly}
              min={0}
              step={0.5}
              fixedPoint={2}
              value={props.data.units}
              onChange={(e) => props.onChange({ units: e.target.value })}
              error={hasError('units')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <VseNumberField
              label='No. of Teaching Hours'
              helperText='per Credit / Unit'
              required
              readOnly={props.readOnly}
              min={0}
              step={0.5}
              fixedPoint={2}
              value={props.data.teachHrsPerUnit}
              onChange={(e) => props.onChange({ teachHrsPerUnit: e.target.value })}
              error={hasError('teachHrsPerUnit')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <VseNumberField
              label='Course Places for VSE'
              helperText='Min: 5 for each course; Max: 70 for each university'
              required
              readOnly={props.readOnly}
              min={0}
              value={props.data.coursePlaces}
              onChange={(e) => props.onChange({ coursePlaces: e.target.value })}
              error={hasError('coursePlaces')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <VseSelectField
              label='Language of Instruction'
              required
              readOnly={props.readOnly}
              value={props.data.language}
              options={props.languageOptions}
              onChange={(e) => props.onChange({ language: e.target.value })}
              error={hasError('language')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <VseTextField
              label="First Day of Class"
              type="date"
              required
              readOnly={props.readOnly}
              value={firstDayOfClass}
              onChange={(e) => props.onChange({ firstDayOfClass: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={hasError('firstDayOfClass')}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <VseTextField
              label="Last Day of Class"
              type="date"
              required
              readOnly={props.readOnly}
              value={lastDayOfClass}
              onChange={(e) => props.onChange({ lastDayOfClass: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              error={hasError('lastDayOfClass')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="link"
              label='Link to Course Syllabus / Outline'
              required
              readOnly={props.readOnly}
              value={props.data.courseLink}
              onUpdated={(content) => {
                props.onChange({ courseLink: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseLink')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Course Description'
              required
              multiline
              rows={10}
              readOnly={props.readOnly}
              value={props.data.courseDescription}
              onUpdated={(content) => {
                props.onChange({ courseDescription: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseDescription')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Course Prerequisites/Restrictions'
              multiline
              rows={4}
              readOnly={props.readOnly}
              value={props.data.courseRestriction}
              onUpdated={(content) => {
                props.onChange({ courseRestriction: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('courseRestriction')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Points to Note for Students'
              multiline
              rows={4}
              readOnly={props.readOnly}
              value={props.data.pointNotes}
              onUpdated={(content) => {
                props.onChange({ pointNotes: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('pointNotes')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} md={12}>
            <VseHtmlField
              mode="content"
              label='Fees to be Borne by Students'
              multiline
              rows={2}
              readOnly={props.readOnly}
              value={props.data.feeByStudents}
              onUpdated={(content) => {
                props.onChange({ feeByStudents: content });
                setEditorProps({ open: false });
              }}
              onEditorDialogReqested={(args) => {
                setEditorProps({
                  open: args.open,
                  onOpen: (openArgs) => Object.assign(openArgs, args),
                });
              }}
              error={hasError('feeByStudents')}
            />
          </Grid>
        </Grid>
      </Grid>
      <VseTinymceEditorDialog {...editorProps} onClose={() => setEditorProps({ open: false })} />
    </div>
  );
}
